import { isOnPremise } from 'bones/utils/portal/environment';

/** https://developers.heap.io/reference */
interface HeapGlobal {
  load: (
    appId: string,
    config?: {
      disableTextCapture: boolean;
    }
  ) => void;

  /** Use this API to send stateful information into Heap as a set of global key-value pairs that get attached to all of a user's subsequent events. */
  addEventProperties?(eventProperties: any): void;
  /** This API allows you to attach custom properties at the user level, such as user-level info from your database or demographic info. */
  addUserProperties?(userProperties: any): void;
  /**
   * This API should be called prior to calling the addEventProperties API.
   * It removes all previously stored event properties from the cookie, which ensures the properties getting added by the next addEventProperties call are always relevant.
   */
  clearEventProperties?(): void;
  /** This API allows you to attach a unique identity and maintain user histories across sessions, devices, and browsers under a single profile in Heap. */
  identify?(identity: string): void;
  /** Use this API to reset a user’s identity to an anonymous state after they have logged out. */
  resetIdentity?(): void;
  /** Use this API to stop a single event property from getting attached to all subsequent events. This API is usually called in relation to addEventProperties. */
  removeEventProperty?(eventProperty: string): void;
  /** Use this API to send custom events and associated metadata to Heap that we do not automatically capture, such as purchase information, email sent, and more. */
  track?(eventName: string, eventProperties?: any): void;

  /** Retrieve the current user’s ID. */
  readonly userId?: string;

  /** The current user’s identity, if identified. */
  readonly identity?: string;
}

declare global {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Window {
    heap?: HeapGlobal;
  }
}

class HeapAgent {
  // istanbul ignore next
  loadHeapGlobal = () => {
    // snippet copied from https://developers.heap.io/docs/web
    // prettier-ignore
    // @ts-ignore
    // eslint-disable-next-line
    window.heap=window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};var r=document.createElement("script");r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);for(var n=function(e){return function(){heap.push([e].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])};
  };

  loaded = false;

  load() {
    const appId = process.env.HEAP_ID;
    if (!appId || isOnPremise()) {
      return;
    }

    if (!this.loaded) {
      this.loadHeapGlobal();

      this.loaded = true;

      window.heap?.load(appId, {
        disableTextCapture: true,
      });
    }

    this.resetEventProperties();
  }

  identify = (identity: string) => {
    window.heap?.identify?.(identity);
  };

  resetEventProperties = ({
    stardogVersion,
    stardogLicenseType,
  }: {
    stardogVersion?: string;
    stardogLicenseType?: string;
  } = {}) => {
    if (isOnPremise()) {
      return;
    }

    window.heap?.clearEventProperties?.();
    window.heap?.addEventProperties?.({
      'Stardog Version': stardogVersion,
      'Stardog License Type': stardogLicenseType,
      'Application Version': process.env.APP_VERSION,
    });
  };
}

export const heapAgent = new HeapAgent();
